import React, { useState } from 'react'

import { isEmpty, toString } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { DataItem, load, navigateTo, urlForDataItem } from 'scrivito'

import { CellInput } from './CellInput'
import { getRowMenu } from './getRowMenu'
import { OpenEntityCanvasAction, TableColumn } from './types'
import { ConfirmationModal } from '../shared/modals/ConfirmationModal'
import { Badge, Variant as BadgeVariant } from '@/Atomic/Atoms/Badge'
import { Avatar } from '@/Atomic/Molecules/Avatar'
import { FileFormatBadge } from '@/Atomic/Molecules/FileFormatBadge'
import {
  TableRow as TableRowElement,
  TableCell as TableCellElement,
} from '@/Atomic/Templates/Table'
import { DataImage } from '@/Components/DataImage'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/typesV2'
import { useDataItemReferenceHandler } from '@/hooks/useDataItemReferenceHandler'
import { useModalHandler } from '@/hooks/useModalHandler'
import {
  isAutomationsItem,
  navigateToAutomations,
} from '@/utils/automationsUtils'
import { useDataItemDelete } from '@/utils/CRUD/hooks/useDataItemDelete'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { getDataItemInitials, isProfileEntity } from '@/utils/dataClassMap'
import { getVariantForEnumValue } from '@/utils/enumGroupVariant'
import { getDisplayValue } from '@/utils/getDisplayValue'
import { getDeleteModalSubtitle } from '@/Widgets/DXPDetails/utils/deleteModalSubtitle'
import { getDeleteModalTitle } from '@/Widgets/DXPDetails/utils/deleteModalTitle'

interface EntityTableRowProps {
  dataItem: DataItem
  schema: DataItem
  tableColumns: TableColumn[]
  defaultImage?: string
  imageAlt?: string
  onNavigateTo?: (dataItem: DataItem) => void
  onOpenInNewTab?: (dataItem: DataItem) => void
  omitSidebar?: boolean
  onOpenEntityCanvasCallbackFn?: OpenEntityCanvasAction
  onOpenDataItemDrawerClick?: (
    e: React.MouseEvent<Element, MouseEvent>,
    dataItem: DataItem,
    startInEdit?: boolean,
  ) => void
  selectRow?: (id: string, select?: boolean) => void
  isRowSelected?: (id: string) => boolean
  isRowSelectDisabled?: (id: string) => boolean
}

export const EntityTableRow: React.FC<EntityTableRowProps> = ({
  dataItem,
  schema,
  tableColumns,
  defaultImage,
  imageAlt,
  onNavigateTo,
  onOpenInNewTab,
  omitSidebar,
  onOpenEntityCanvasCallbackFn,
  onOpenDataItemDrawerClick = () => {},
  selectRow,
  isRowSelected,
  isRowSelectDisabled,
}) => {
  const { t } = useTranslation()
  const { deleteDataItem } = useDataItemDelete(dataItem)
  const { isOpen, closeModal, openModal: openDeleteModal } = useModalHandler()
  const [rowMenuTrigger, setRowMenuTrigger] = useState<boolean>()

  const dataClassName = dataItem.dataClassName()!
  const deleteModalTitle = getDeleteModalTitle(dataClassName)
  const deleteModalSubtitle = getDeleteModalSubtitle(dataClassName)

  const automationsItem = isAutomationsItem(dataItem)

  const onOpenEntityCanvasClick = (
    e: React.MouseEvent,
    startInEdit: boolean = false,
  ) => {
    if (!omitSidebar && !automationsItem) {
      if (onOpenEntityCanvasCallbackFn) {
        onOpenEntityCanvasCallbackFn(
          e,
          dataItem,
          startInEdit,
          onOpenDataItemDrawerClick,
        )
      } else {
        onOpenDataItemDrawerClick(e, dataItem, startInEdit)
      }
    }
  }

  const onNavigateToClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (onNavigateTo) {
      onNavigateTo(dataItem)
    } else if (automationsItem) {
      navigateToAutomations(dataItem)
    } else {
      navigateTo(dataItem)
    }
  }

  const onOpenInNewTabClick = () => {
    if (onOpenInNewTab) {
      onOpenInNewTab(dataItem)
    } else if (automationsItem) {
      navigateToAutomations(dataItem, true)
    } else {
      load(() => {
        urlForDataItem(dataItem)
      }).then(() => {
        const url = urlForDataItem(dataItem) || undefined
        window.open(url, '_blank')
      })
    }
  }

  const onRowMenuTrigger = (e: React.MouseEvent) => {
    e.preventDefault()
    setRowMenuTrigger(!rowMenuTrigger)
  }

  const rowMenu = getRowMenu({
    dataItem,
    t,
    omitSidebar,
    onNavigateToClick,
    onOpenInNewTabClick,
    onOpenEntityCanvasClick,
    openDeleteModal,
  })

  return (
    <>
      <TableRowElement
        className={
          !omitSidebar && !automationsItem ? 'cursor-pointer' : undefined
        }
        onClick={onOpenEntityCanvasClick}
        onDoubleClick={onOpenInNewTabClick}
        onRightClick={onRowMenuTrigger}
      >
        {tableColumns.map((col, index) => (
          <TableCellElement
            key={`${col.accessor}-${index}`}
            variant={col.variant}
            className={col.accessor ? `data-table-col-${col.accessor}` : ''}
            noWrap={col.noWrap}
            onClick={onNavigateToClick}
            dataLabel={col.title}
            dataItem={dataItem}
            actions={rowMenu}
            actionsTrigger={rowMenuTrigger}
            rowId={dataItem.id()}
            selectRow={selectRow}
            isRowSelected={isRowSelected}
            isRowSelectDisabled={isRowSelectDisabled}
          >
            <TableCell
              col={col}
              dataItem={dataItem}
              schema={schema}
              imageAlt={imageAlt}
              defaultImage={defaultImage}
            />
          </TableCellElement>
        ))}
      </TableRowElement>
      <ConfirmationModal
        variant="danger"
        isOpen={isOpen}
        close={closeModal}
        confirm={() => deleteDataItem()}
        title={t(deleteModalTitle)}
        subtitle={t(deleteModalSubtitle)}
      />
    </>
  )
}

interface TableCellProps {
  col: TableColumn
  dataItem: DataItem
  schema: DataItem
  image?: DataBinary
  defaultImage?: string
  imageAlt?: string
}
const TableCell: React.FC<TableCellProps> = ({
  dataItem,
  col,
  schema,
  imageAlt,
  defaultImage,
}) => {
  const { add, get } = useDataItemReferenceHandler()

  if (col.cell) {
    const Cell = col.cell
    return <Cell dataItem={dataItem} column={col} schema={schema} />
  }

  if (!col.accessor) return null

  const rawValue = dataItem.get(col.accessor)

  if (col.accessor === 'image') {
    const dataClassName = dataItem.dataClassName()

    if (isProfileEntity(dataClassName || '')) {
      return (
        <Avatar
          avatar={dataItem.get('image') as DataBinary}
          icon={dataClassName === DataClassName.COMPANY ? 'building' : 'user'}
          size="lg"
          initials={getDataItemInitials(dataItem)}
          initialsColorSeed={dataItem.id()}
        />
      )
    }

    return (
      <DataImage
        dataBinary={dataItem.get('image') as DataBinary}
        className="img-profile sm cover"
        alt={imageAlt}
        defaultImage={defaultImage || ''}
      />
    )
  }

  if (col.accessor === 'format') {
    if (dataItem.dataClassName() === DataClassName.DOCUMENT) {
      const format = typeof rawValue === 'string' ? rawValue.trim() : ''
      if (isEmpty(format)) return '-'
      return <FileFormatBadge format={format} />
    }
  }

  if (col.accessor === 'labels') {
    const labels = (rawValue || []) as Array<string>
    if (isEmpty(labels)) return '-'
    return (
      <>
        {labels.map((label, index) => {
          return (
            <Badge
              key={`${index}`}
              value={label}
              variant={getVariantForLabel(label)}
            />
          )
        })}
      </>
    )
  }

  const value = isEmpty(toString(rawValue))
    ? ''
    : getDisplayValue({
        rawValue,
        key: col.accessor,
        schema,
        add,
        get,
      })

  if (col.accessor === 'status') {
    if (!value) return '-'
    const enumValues = (schema.get('attributes') as SchemaAttributes)?.[
      col.accessor
    ]?.enumValues
    const variant = getVariantForEnumValue(enumValues, rawValue)

    return (
      <CellInput dataItem={dataItem} schema={schema} col={col}>
        <Badge value={value} variant={variant} />
      </CellInput>
    )
  }

  if (col.accessor === 'priority') {
    if (!value) return '-'
    let variant: BadgeVariant
    switch (value) {
      case 'Low':
        variant = 'success'
        break
      case 'Normal':
        variant = 'warning'
        break
      case 'High':
        variant = 'danger'
        break
      default:
        variant = 'primary'
        break
    }

    return <Badge value={value} variant={variant} />
  }

  if (col.accessor === 'type' && schema.get('name') === 'list') {
    if (!value) return '-'
    return <Badge value={value} variant="info" />
  }

  return value || '-'
}

const getVariantForLabel = (label: string): BadgeVariant => {
  switch (label) {
    case 'duplicate':
      return 'danger'
    case 'imported':
      return 'warning'
    default:
      return 'primary'
  }
}
